'use client';

// Pull all shared types directly from the SDK source — no duplication needed.
// All imports here are type-only so no SDK runtime code is bundled.
export type {
  AgentSessionEvent,
  AgentStatus,
  AppInfo,
  AppPathName,
  AppStateKey,
  ContentRegistryConfig,
  ContentRegistryMutationResult,
  ContentRegistryView,
  DeleteLocalContentResult,
  DownloadContentResult,
  ElectronAPI,
  FileServerStatus,
  GenesysSdkEnvironment,
  LagSimulationConfig,
  LogCallback,
  ModelInfo,
  NetSettingsConfig,
  Notification,
  NotificationCallback,
  NotificationLevel,
  BlenderCheckResult,
  LightmapHarvestReadyReason,
  PnpmCheckResult,
  ProjectOpenRequest,
  ProjectOpenResult,
  ProjectInfo,
  ScenarioCredentialStatus,
  ScenarioForwardRequestPayload,
  ScenarioForwardResponse,
  ScenarioMutationResult,
  ScenarioRevealedCredentials,
  ScenarioUploadRequestPayload,
  UpdaterPhase,
  UpdaterState,
  UpdaterTrigger,
  SessionInfo,
  AgentMessage,
  ContextUsage,
  SessionStats,
  GatewayEvent,
  GatewayAgentInfo,
  GatewayChangedFile,
  GatewayErrorKind,
  GatewayStatus,
  GatewayConnectionTestResult,
  GatewayCredentialStatus,
  GatewayDevActionResult,
  GatewayModelInfo,
  GatewayModelParameterDefinition,
  GatewayModelParameterSelection,
  GatewayModelParameterValue,
  GatewayModelSelection,
  GatewayModelVariant,
  GatewayMcpApprovalDecision,
  GatewayMcpApprovalDefaultMode,
  GatewayMcpApprovalRequest,
  GatewayMcpApprovalSettings,
  GatewayMcpApprovalToolMode,
  GatewayMcpDevDocId,
  GatewayMcpDevDocResult,
  GatewayMcpStatus,
  GatewayRunMode,
  GatewayStepEvent,
  GatewaySetCredentialResult,
  GatewaySettings,
  GatewaySummaryEvent,
  GatewayTranscriptMessage,
  GatewayTranscriptRole,
  GatewayTranscriptToolCall,
  GatewayTranscriptUsage,
  AgentBackendMode,
  AgentBackendDescriptor,
  ThinkingLevel,
} from '../../../../packages/sdk/src/electron/api';

export type {
  ProjectFolderAndFile,
  ProjectTemplate,
  ToolCallingResult,
} from '../../../../packages/sdk/src/core/types';

export type {
  AssetPackContents,
  AssetPackInstallOptions,
  AssetPackInstallResult,
  AssetPackLicenseChoice,
  AssetPackLicenseTemplateId,
  AssetPackMissingDependency,
  AssetPackVersionBumpKind,
  ExportInProjectAssetPackOptions,
  ExportInProjectAssetPackResult,
  InProjectAssetPackSummary,
  NewInProjectAssetPackOptions,
  NewInProjectAssetPackResult,
} from '../../../../packages/sdk/src/core/tools/asset-pack-types';

export { PACKS_FOLDER_NAME, PACKS_TS_NAMESPACE } from '../../../../packages/sdk/src/core/const';

export type {
  GenesysContent,
  GenesysContentMetadata,
  GenesysContentType,
} from '../../../../packages/sdk/src/core/content/content-types';

import type { AppInfo, ElectronAPI } from '../../../../packages/sdk/src/electron/api';

export let sdk: ElectronAPI | undefined;
export let appInfo: AppInfo | undefined;
export let platform: string | undefined;

export function getSdk(win: any): ElectronAPI | undefined {
  return win?.electronAPI;
}

export const isSdkWindow = () => !!getSdk(window);
export const isSdkParentWindow = () => !!getSdk(window?.opener);
export const isMac = () => platform === 'darwin';

export const initSdk = async () => {
  sdk = getSdk(window);
  let lastOpenedProjectPath: string | undefined;
  if (sdk) {
    appInfo = await sdk.app.getInfo();
    platform = await sdk.os.platform();
    lastOpenedProjectPath = await sdk.app.getState('project.lastOpenedDirectory');
  }
  return { sdk, appInfo, platform, lastOpenedProjectPath };
};

export const getAppInfo = async () => {
  if (sdk) {
    appInfo = await sdk.app.getInfo();
  }
  return appInfo;
};
